import { useEffect, useRef, useState, useCallback } from "react";
import { Paper, Box, CircularProgress } from "@mui/material";
import theme from "../../styles/Themecolors";

/**
 * Custom infinite scroll hook specifically for Opening Stock dropdown
 * Provides smooth scrolling with preserved scroll position
 */
const useInfiniteScrollOpeningStock = (fetchData, row, setRow, searchTerm = '') => {
   
  const isFetchingRef = useRef(false);
  const scrollPositionRef = useRef(0);
  const listboxRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
   
  const handleScroll = useCallback((event) => {
    const listboxNode = event.target;
    listboxRef.current = listboxNode;
    
    // Store current scroll position for preservation
    scrollPositionRef.current = listboxNode.scrollTop;
    
    // More reliable bottom detection with buffer
    const scrollTop = listboxNode.scrollTop;
    const scrollHeight = listboxNode.scrollHeight;
    const clientHeight = listboxNode.clientHeight;
    
    // Check if user is near the bottom (within 50px buffer)
    const threshold = 50;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    if (isNearBottom && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsLoading(true);
      
      setRow((prev) => {
        const newRow = prev + 20;
        
        // Call fetchData with the updated value and preserved search term
        // fetchData doesn't return a promise, so we handle it differently
        fetchData(searchTerm, newRow, 20);
        
        return newRow;
      });
    }
  }, [searchTerm, fetchData]);
  
  // Reset fetching flag when loading completes and restore scroll position
  useEffect(() => {
    if (!isLoading && isFetchingRef.current) {
      isFetchingRef.current = false;
      
      // Restore scroll position after data load to prevent jumps
      if (listboxRef.current && scrollPositionRef.current > 0) {
        setTimeout(() => {
          if (listboxRef.current) {
            listboxRef.current.scrollTop = scrollPositionRef.current;
          }
        }, 0);
      }
    }
  }, [isLoading]);
  
  const ListboxProps = {
    onScroll: isLoading === false ? handleScroll : undefined,
    sx: {
      maxHeight: "300px",
      overflowY: "auto",
      scrollbarWidth: "thin",
      scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.default}`, // Firefox
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.background.default,
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
      scrollBehavior: "smooth", // Enable smooth scrolling
      paddingTop: "8px", // Ensure first item has space from top
      paddingBottom: "4px", // Ensure last item has space from bottom
    },
  };

  const PaperComponent = ({ children, ...props }) => (
    <Paper
      {...props}
      sx={{
        maxWidth: '455px',
        width: 'fit-content', // grows with content
        minWidth: '100%',     // match input width
        overflow: 'visible',  // Allow dropdown to expand naturally
        maxHeight: 'none',    // Remove height constraint to prevent jumps
      }}
    >
      {children}
    </Paper>
  );

  return { setIsLoading, ListboxProps, PaperComponent };
};

export default useInfiniteScrollOpeningStock;
